import { esc, fmtMoney } from "../ui/format.js";
import { uiGet, uiMoney } from "../ui/kit.js";
import { toast } from "../ui/modals.js";

/**
 * Company Wrapped (backend/wrapped.py): the year in review as a story.
 *
 * openWrapped(year) plays a sequence of full-screen cards (they advance on
 * their own; tap right for next, left for back, Esc to close): the revenue
 * story, profit and cash, what you built, deals and money, the drama
 * (Signature Moments, the nemesis, incidents), people, then who you were as
 * a CEO. The last card is a summary you can save as an image or copy as text.
 * renderWrappedInto(host) lists the years in the CEO Office.
 */
const WR_STEP_MS = 4200;
const WR_COLORS = ["#7ee8fa", "#a78bfa", "#34d399", "#fbbf24", "#f472b6", "#fb7185", "#9fb4ff", "#e5b44f"];
const wrM = (v) => uiMoney(v, { compact: true }); // fmtMoney has no compact form: it printed every digit

function wrSpark(series, color) {
  if (!series || series.length < 2) return "";
  const W = 520, H = 120, max = Math.max(...series, 1), min = Math.min(0, ...series);
  const X = (i) => (W * i) / (series.length - 1), Y = (v) => H - ((v - min) / (max - min || 1)) * (H - 10) - 5;
  const d = series.map((v, i) => `${i ? "L" : "M"}${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(" ");
  return `<svg class="wr-spark" viewBox="0 0 ${W} ${H}"><path d="${d} L${W},${H} L0,${H} Z" fill="${color}" fill-opacity=".15"/><path d="${d}" fill="none" stroke="${color}" stroke-width="4" stroke-linejoin="round" class="wr-draw"/></svg>`;
}

function wrList(items, empty) {
  return items.length ? `<ul class="wr-list">${items.slice(0, 6).map((x) => `<li>${x}</li>`).join("")}${items.length > 6 ? `<li class="wr-more">+${items.length - 6} more</li>` : ""}</ul>` : `<p class="wr-empty">${esc(empty)}</p>`;
}

function wrSlides(y) {
  const r = y.revenue, p = y.profit, c = y.cash;
  const growth = r.growth_multiple ? (r.growth_multiple - 1) * 100 : null;
  return [
    { bg: 0, html: `<div class="wr-kick">${esc(y.company)} · ${esc(y.industry)}</div><h1>Your Year ${y.year}<br><span>Wrapped</span></h1><p>${y.partial ? `The year so far: months ${y.months[0]}–${y.months[1]}` : `Months ${y.months[0]}–${y.months[1]}`}</p>` },
    { bg: 1, html: `<div class="wr-kick">Revenue</div><div class="wr-big">${wrM(r.total)}</div><p>came in this year${growth != null ? `, and your monthly revenue <b>${growth >= 0 ? "grew" : "shrank"} ${Math.abs(Math.round(growth))}%</b>` : ""}.</p>${wrSpark(r.series, "#7ee8fa")}<p class="wr-small">Best month: M${r.best_month}, ${wrM(r.best)}</p>` },
    { bg: 2, html: `<div class="wr-kick">Profit & cash</div><div class="wr-big ${p.total >= 0 ? "pos" : "neg"}">${wrM(p.total)}</div><p>${p.total >= 0 ? "profit" : "lost"} over the year · <b>${p.profitable_months}</b> profitable month${p.profitable_months === 1 ? "" : "s"}${p.first_profitable_month ? ` (the first was M${p.first_profitable_month})` : ""}.</p>${wrSpark(p.series, p.total >= 0 ? "#34d399" : "#fb7185")}<p class="wr-small">Cash hit its low in M${c.low_month} (${wrM(c.low)}) and ended at ${wrM(c.end)}.</p>` },
    { bg: 3, html: `<div class="wr-kick">What you built</div><div class="wr-big">${y.launches.length}</div><p>product${y.launches.length === 1 ? "" : "s"} launched${y.countries.length ? ` · ${y.countries.length} new market${y.countries.length === 1 ? "" : "s"}` : ""}</p>${wrList([...y.launches.map((l) => `🚀 ${esc(l.name)} <em>M${l.month}</em>`), ...y.countries.map((x) => `🌍 ${esc(x.country)} <em>M${x.month}</em>`)], "No launches this year: the year of focus.")}` },
    { bg: 4, html: `<div class="wr-kick">Deals & money</div><div class="wr-big">${y.deals.length + y.acquisitions.length}</div><p>deal${y.deals.length + y.acquisitions.length === 1 ? "" : "s"} done</p>${wrList([...y.deals.map((d) => `🤝 ${esc(d.company)} <em>M${d.month}</em>`), ...y.acquisitions.map((a) => `🏢 Bought ${esc(a.name)} <em>M${a.month}</em>`), ...y.money_raised.map((m) => `💰 ${m.type === "ipo_completed" ? "IPO" : "Raised"} ${m.amount ? wrM(m.amount) : ""} <em>M${m.month}</em>`)], "No deals signed. Next year, take more meetings.")}${y.valuation.end ? `<p class="wr-small">Valuation ${wrM(y.valuation.start)} → <b>${wrM(y.valuation.end)}</b></p>` : ""}` },
    { bg: 5, html: `<div class="wr-kick">The drama</div><h2>${y.moments.length + y.nemesis.length + y.incidents.length ? "It wasn't quiet." : "A calm year."}</h2>${wrList([...y.moments.map((m) => `${m.success ? "⭐" : "💥"} ${esc(m.title)} <em>${m.success ? "nailed it" : "went wrong"} · M${m.month}</em>`), ...y.nemesis.map((n) => `⚔ ${n.type === "nemesis_emerged" ? `${esc(n.who)} declared you the enemy` : n.type === "nemesis_beaten" ? `You beat ${esc(n.who)}` : `You answered ${esc(n.who)}`} <em>M${n.month}</em>`), ...y.incidents.map((i) => `⚡ ${esc(i.type)} <em>M${i.month}</em>`)], "No crises, no feuds, no surprises.")}` },
    { bg: 6, html: `<div class="wr-kick">People</div><div class="wr-big">${y.people.hired}</div><p>leader${y.people.hired === 1 ? "" : "s"} hired · ${y.people.left} left</p><p class="wr-small">Morale ends the year at <b>${y.people.morale_end}</b>/100${y.board ? ` · the Board is <b>${esc(y.board.verdict)}</b> (${y.board.confidence})` : ""}.</p>` },
    ...((y.figures || []).length ? [{ bg: 3, html: `<div class="wr-kick">The year in numbers</div><div class="wr-figs">${y.figures.map((f) => `<div><span>${esc(f.label)}</span>
      <b>${f.kind === "money" ? wrM(f.value) : f.kind === "pct" ? `${f.value}%` : Number(f.value).toLocaleString("en-US")}</b><em>${esc(f.note || "")}</em></div>`).join("")}</div>` }] : []),
    { bg: 7, html: `<div class="wr-kick">This year, you were</div><h1 class="wr-persona">${esc(y.persona.title)}</h1><p>${esc(y.persona.line)}</p>` },
    { bg: 0, final: true, html: wrCard(y) },
  ];
}

function wrCard(y) {
  return `<div class="wr-card"><div class="wr-kick">${esc(y.company)} · Year ${y.year} Wrapped</div><h2>${esc(y.persona.title)}</h2>
    <div class="wr-grid"><div><span>Revenue</span><b>${wrM(y.revenue.total)}</b></div><div><span>Profit</span><b>${wrM(y.profit.total)}</b></div>
      <div><span>Launches</span><b>${y.launches.length}</b></div><div><span>Deals</span><b>${y.deals.length + y.acquisitions.length}</b></div></div>
    <p>${esc(y.headline.join(" · "))}</p>
    <div class="wr-actions"><button type="button" class="primary" data-wr="save">Save image</button><button type="button" data-wr="copy">Copy summary</button><button type="button" data-wr="close">Done</button></div></div>`;
}

/** The final card as a 1080x1350 PNG (drawn on a canvas; no external fonts). */
function wrSaveImage(y) {
  const c = document.createElement("canvas");
  c.width = 1080; c.height = 1350;
  const g = c.getContext("2d");
  const grad = g.createLinearGradient(0, 0, 1080, 1350);
  grad.addColorStop(0, "#14183a"); grad.addColorStop(1, "#3a1640");
  g.fillStyle = grad; g.fillRect(0, 0, 1080, 1350);
  g.fillStyle = "#7ee8fa"; g.font = "600 34px sans-serif"; g.fillText(`${y.company} · Year ${y.year} Wrapped`, 80, 140);
  g.fillStyle = "#ffffff"; g.font = "bold 96px serif"; g.fillText(y.persona.title, 80, 280);
  g.fillStyle = "#c9d2ea"; g.font = "36px sans-serif"; g.fillText(y.persona.line, 80, 350);
  [["Revenue", wrM(y.revenue.total)], ["Profit", wrM(y.profit.total)], ["Launches", `${y.launches.length}`], ["Deals", `${y.deals.length + y.acquisitions.length}`]].forEach(([k, v], i) => {
    const x = 80 + (i % 2) * 470, yy = 520 + Math.floor(i / 2) * 260;
    g.fillStyle = "rgba(255,255,255,0.08)"; g.fillRect(x, yy - 90, 430, 210);
    g.fillStyle = "#a8b3d6"; g.font = "32px sans-serif"; g.fillText(k, x + 30, yy - 30);
    g.fillStyle = WR_COLORS[i]; g.font = "bold 76px monospace"; g.fillText(v, x + 30, yy + 70);
  });
  g.fillStyle = "#c9d2ea"; g.font = "32px sans-serif"; g.fillText(y.headline.join(" · ").slice(0, 60), 80, 1110);
  g.fillStyle = "#6b7495"; g.font = "28px monospace"; g.fillText("GES · Global Enterprise Simulator", 80, 1260);
  c.toBlob((blob) => {
    if (!blob) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${y.company.replace(/\W+/g, "-")}-year-${y.year}-wrapped.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }, "image/png");
}

async function openWrapped(year) {
  const y = await uiGet(`/wrapped?year=${year}`);
  const slides = wrSlides(y);
  const reduced = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const el = document.createElement("div");
  el.className = "wr";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-label", `Year ${y.year} Wrapped`);
  el.innerHTML = `<div class="wr-bars">${slides.map(() => `<i><b></b></i>`).join("")}</div><div class="wr-stage"></div><button type="button" class="wr-x" data-wr="close" aria-label="Close">✕</button>`;
  document.body.appendChild(el);
  let i = -1, timer = null;
  const close = () => { clearTimeout(timer); el.remove(); document.removeEventListener("keydown", onKey); };
  const show = (k) => {
    i = Math.max(0, Math.min(slides.length - 1, k));
    const sl = slides[i];
    el.style.setProperty("--wr-bg", WR_COLORS[sl.bg % WR_COLORS.length]);
    el.querySelector(".wr-stage").innerHTML = `<div class="wr-slide${sl.final ? " wr-final" : ""}">${sl.html}</div>`;
    el.querySelectorAll(".wr-bars i").forEach((bar, j) => { bar.className = j < i ? "done" : j === i ? (reduced || sl.final ? "done" : "now") : ""; });
    clearTimeout(timer);
    if (!sl.final && !reduced) timer = setTimeout(() => show(i + 1), WR_STEP_MS);
  };
  const onKey = (e) => {
    if (e.key === "Escape") close();
    else if (e.key === "ArrowRight" || e.key === " ") show(i + 1);
    else if (e.key === "ArrowLeft") show(i - 1);
  };
  document.addEventListener("keydown", onKey);
  el.addEventListener("click", (e) => {
    const b = e.target.closest("[data-wr]");
    if (b) {
      const a = b.dataset.wr;
      if (a === "close") close();
      else if (a === "save") wrSaveImage(y);
      else if (a === "copy") {
        const text = `${y.company} · Year ${y.year} Wrapped: ${y.persona.title}. Revenue ${wrM(y.revenue.total)}, profit ${wrM(y.profit.total)}, ${y.launches.length} launches, ${y.deals.length + y.acquisitions.length} deals. ${y.headline.join(" · ")}`;
        (navigator.clipboard ? navigator.clipboard.writeText(text) : Promise.reject()).then(() => toast("Copied."), () => toast(text));
      }
      return;
    }
    if (slides[i].final) return;
    show(e.clientX < window.innerWidth * 0.3 ? i - 1 : i + 1);
  });
  show(0);
  return el;
}

async function renderWrappedInto(host) {
  const av = await uiGet("/wrapped");
  host.innerHTML = `<div class="wr-years">
    <p class="dy-note">Every 12 months your company gets its year in review, built from what really happened. Tap a year to play it.</p>
    ${av.years.length ? av.years.slice().reverse().map((y) => `<button type="button" class="wr-year${y.partial ? " partial" : ""}" data-wr-year="${y.year}">
      <span>🎁</span><b>Year ${y.year}</b><em>${y.partial ? `so far · months ${y.months[0]}–${y.months[1]}` : `months ${y.months[0]}–${y.months[1]}`}</em><u>Play ▸</u></button>`).join("")
      : `<p class="wr-empty">Play your first month and your Year 1 story starts.</p>`}</div>`;
  host.querySelectorAll("[data-wr-year]").forEach((b) => b.addEventListener("click", () => openWrapped(+b.dataset.wrYear).catch((e) => toast(e.message, true))));
}

window.openWrapped = openWrapped;
window.renderWrappedInto = renderWrappedInto;

export { openWrapped, renderWrappedInto };
